// #3878 — Firebase readiness guard for couch login.
//
// attemptCouchLogin_innerAsync used to read fbAuth.currentUser synchronously and bail
// "NoFirebaseUser" if it was null. But on the warm-resume fast path (#3184) and other
// callers that don't pre-await authStateReady(), Firebase has not finished restoring the
// persisted session yet, so currentUser is transiently null and the sync login fails
// spuriously. (NoFirebaseUser was the largest couch_login_failure bucket: 231 users/30d.)
//
// This races authStateReady() against a bounded timeout — the same shape already used in
// cloud-functions.service.ts:212 — and then reports whether a Firebase user is present,
// plus whether auth actually settled before the timeout. The "settled" flag lets the caller
// split "still settling / hung" (settled === false) from "auth fully settled with no user"
// (settled === true) — the latter being either an ordinary logged-out user or a rejected /
// expired session (e.g. an accounts:lookup 400). It narrows, but does not pinpoint, a rejection.
//
// Pure module (no Angular / Firebase deps) so it runs under node for unit tests — same
// pattern as couch-login-stuck.ts. auth.service.ts holds the state and side effects; this
// holds only the wait + presence decision.

import { describeError } from '@cf-shared/error-describe';
import { ClearTimeoutFn, ScheduleTimeout } from './local-store-timeout';
import { realTimerEnvironment, RunningTimeTimeoutHandle, scheduleRunningTimeTimeout, TimerEnvironment } from './running-clock';

// plain wall time: what every caller got before #6096, and what the couch login's own wait still uses
const WALL_TIME_TIMER = {
  schedule: (cb: () => void, ms: number) => setTimeout(cb, ms),
  clear: (handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** The slice of Firebase Auth this guard needs. */
export interface FbAuthLike {
  authStateReady(): Promise<void>;
  readonly currentUser: object | null;
}

export interface FirebaseUserReadiness {
  /** Is a Firebase user present once auth has settled (or the wait timed out)? */
  present: boolean;
  /** Did authStateReady() resolve before the timeout fired? false = still settling / hung. */
  settled: boolean;
}

/**
 * #3904 — cold-start boot must not wait unbounded on Firebase auth-init.
 *
 * On a cold start the app awaits `fbAuth.authStateReady()` before it can route.
 * `authStateReady()` only resolves once the SDK's `initializeCurrentUser()`
 * finishes — which calls `accounts:lookup` (user reload). When the SDK gets into
 * an unbounded `accounts:lookup` retry storm during that init (the Android
 * boot-hang cohort on Sentry FreezeWatchdog:BootLog), init never finishes,
 * `authStateReady()` never resolves, and a bare `await authStateReady()` wedges
 * the whole boot forever.
 *
 * This bounds the wait, returning whether auth settled before the timeout.
 * `false` = still settling / hung: the caller proceeds with boot anyway and lets
 * the later `onAuthStateChanged` fire route the user once (if) Firebase eventually
 * settles — never blocking first paint on a storming SDK.
 *
 * Never throws: a pathological `authStateReady()` rejection counts as
 * "not settled" (and is swallowed so it can't leak as an unhandled rejection
 * when the timeout wins the race).
 *
 * `timer` decides what the limit counts. The default is plain wall time, which the couch
 * login's own 5 s wait uses. The boot's own waits no longer come through here: since #6079
 * they use `measureAuthStateSettleAsync` below, which always counts running time (#6096).
 */
export async function awaitAuthStateSettledAsync(
  fbAuth: Pick<FbAuthLike, 'authStateReady'>,
  timeoutMs: number,
  timer: { schedule: ScheduleTimeout, clear: ClearTimeoutFn } = WALL_TIME_TIMER,
): Promise<boolean> {
  let timeoutHandle: unknown;
  let settled = false;
  try {
    // race auth settling against the timeout; whichever wins, we return the flag.
    // the rejection handler on authStateReady() swallows a pathological rejection
    // (token errors normally go to the onAuthStateChanged error callback, not here)
    // and also prevents an unhandled rejection if the timeout wins the race first.
    await Promise.race([
      fbAuth.authStateReady().then(() => { settled = true; }, () => { /* stays unsettled */ }),
      new Promise<void>(resolve => { timeoutHandle = timer.schedule(resolve, timeoutMs); }),
    ]);
  } finally {
    if (timeoutHandle !== undefined) timer.clear(timeoutHandle);
  }
  return settled;
}

// #6079: what a timed-out wait knows about itself.
//
// BootAuthStateReadyTimeout used to say only "Firebase did not settle in 8 s". Over 100
// production reports that one name covered a page hidden in the background, a Firebase that
// finished a moment after the limit, and one that never finished — and most "online-flip"
// reports turned out to be waits during the first seconds of a warm-resume boot. These
// measurements let a report say which. design-docs/telemetry.md, "BootAuthStateReadyTimeout".

/** Google identity requests that FINISHED during the wait, read from the browser's resource timing list. */
export interface FirebaseRequestsSeen {
  /** false when the browser has no resource timing list, so "no requests" means nothing. */
  available: boolean;
  /** Entries in the whole list. The browser stops recording once it is full (150 by default). */
  bufferEntries: number;
  /** `<host><path> +<started after the wait began>ms <took>ms`; never the query string, which carries the API key. */
  requests: string[];
}

/** The slice of a `PerformanceResourceTiming` entry this needs. */
export interface ResourceTimingLike {
  name: string;
  startTime: number;
  duration: number;
}

/** What `measureAuthStateSettleAsync` reads from the page. Injected so tests can drive it by hand. */
export interface AuthSettleEnvironment {
  /** The monotonic clock (`performance.now()`) and timers. */
  timers: TimerEnvironment;
  isHidden(): boolean;
  /** Calls `cb` whenever the page becomes hidden; returns a function that stops listening. */
  onHidden(cb: () => void): () => void;
  firebaseRequestsSince(startMs: number): FirebaseRequestsSeen;
  /** The last failed Firebase Auth request the fetch wrapper saw, if any (#6174). */
  lastFirebaseFetchFailure(): FirebaseFetchFailure | null;
}

/** How a Firebase wait ended. */
export enum AuthSettleEnd {
  /** authStateReady() resolved before the limit. */
  Settled = 'settled',
  /** the limit fired first. */
  TimedOut = 'timed-out',
  /** authStateReady() rejected before the limit; Firebase normally reports errors elsewhere. */
  Rejected = 'rejected',
}

export interface AuthSettleMeasurement {
  /** Did authStateReady() resolve before the limit fired? */
  settled: boolean;
  /** Why the wait ended: tells a timeout from a rejection, which are both "not settled". */
  end: AuthSettleEnd;
  /** What authStateReady() rejected with, described for a report; null unless it rejected before the limit. */
  rejection: string | null;
  /** Real elapsed time of the wait. */
  waitWallMs: number;
  /** Time the page was frozen during the wait, so it did not count towards the limit. */
  notRunningMs: number;
  /** Was the page hidden when the wait began, or at any moment during it? */
  hiddenDuringWait: boolean;
  /** How long the page had been loaded when the wait began: tells a boot-time wait from a later one. */
  pageAgeMs: number;
  /** Read only when the wait did not settle; null when it settled. */
  firebaseRequests: FirebaseRequestsSeen | null;
  /** The last failed Firebase Auth request, when the wait did not settle and it failed recently enough to matter (#6174). */
  firebaseLastFetchFailure: string | null;
}

// the hosts and the path Firebase Auth's start-up talks to: the user lookup, the token
// refresh, Google's script loader, and the sign-in iframe on the auth domain
const FIREBASE_AUTH_HOSTS = ['identitytoolkit.googleapis.com', 'securetoken.googleapis.com', 'apis.google.com'];
const FIREBASE_AUTH_IFRAME_PATH_PREFIX = '/__/auth/';
// a stuck start-up makes a few requests at most; this only bounds a pathological list
const MAX_REPORTED_FIREBASE_REQUESTS = 10;
// scheme, then the host (group 1, without a port), then the path (group 2); the query string and fragment are dropped
const HTTP_URL_HOST_AND_PATH = /^https?:\/\/([^/?#:]+)(?::\d+)?([^?#]*)/i;

/** `<host><path>` of an http(s) URL, without port, query string or fragment; null for anything else. */
function hostAndPath(url: string): { host: string, urlPath: string } | null {
  const parts = HTTP_URL_HOST_AND_PATH.exec(url);
  if (!parts) return null;
  return { host: parts[1].toLowerCase(), urlPath: parts[2] || '/' };
}

/**
 * Whether the URL goes to one of Firebase Auth's own hosts (#6174). The global fetch wrapper uses it
 * to hand those failures straight back, so it shares this module's host list with the timeout report.
 */
export function isFirebaseAuthUrl(url: string): boolean {
  const parts = hostAndPath(url);
  return parts !== null && FIREBASE_AUTH_HOSTS.includes(parts.host);
}

/**
 * Pick the Google identity requests that finished at or after `startMs` out of a resource timing list.
 *
 * #6174: a request that started before the wait but finished during it is kept, with a negative
 * start offset. Leaving those out made 18 of the first 20 v1.34.0 reports read "no request".
 */
export function summarizeFirebaseRequests(entries: ResourceTimingLike[] | null, startMs: number): FirebaseRequestsSeen {
  // if the browser has no resource timing list, say so rather than report "no requests"
  if (!entries) return { available: false, bufferEntries: 0, requests: [] };
  const requests: string[] = [];
  for (const entry of entries) {
    // if it finished before the wait began, it says nothing about the wait
    if (entry.startTime + entry.duration < startMs) continue;
    // anything not http(s) (a data: or blob: entry) is not a Firebase request
    const parts = hostAndPath(entry.name);
    if (!parts) continue;
    // if it is neither a Firebase host nor the auth iframe, skip it
    if (!FIREBASE_AUTH_HOSTS.includes(parts.host) && !parts.urlPath.startsWith(FIREBASE_AUTH_IFRAME_PATH_PREFIX)) continue;
    requests.push(`${parts.host}${parts.urlPath} +${Math.round(entry.startTime - startMs)}ms ${Math.round(entry.duration)}ms`);
    if (requests.length >= MAX_REPORTED_FIREBASE_REQUESTS) break;
  }
  return { available: true, bufferEntries: entries.length, requests };
}

/** A Firebase Auth request that failed at the network level, as the global fetch wrapper saw it. */
export interface FirebaseFetchFailure {
  /** `performance.now()` when the wrapper saw the failure. */
  atMs: number;
  /** `<host><path>`; never the query string, which carries the API key. */
  where: string;
  /** The error's name (`TypeError`, `TimeoutError`); never its message or the request body. */
  errorName: string;
}

/**
 * #6174: the last failed Firebase Auth request, so a sign-in timeout report can name it. A failed
 * request may never appear in the browser's resource timing list, so that list alone cannot show it.
 * The global fetch wrapper (`handleFetchRejectionAsync`) notes each failure here.
 */
export class FirebaseFetchFailureLog {
  private last: FirebaseFetchFailure | null = null;

  // record where the failed request went and the error's name; never its query string, body or message
  note(url: string, error: unknown, atMs: number): void {
    const parts = hostAndPath(url);
    const name = (error as { name?: unknown } | null)?.name;
    this.last = {
      atMs,
      where: parts ? `${parts.host}${parts.urlPath}` : 'unknown',
      errorName: typeof name === 'string' ? name : 'unknown',
    };
  }

  latest(): FirebaseFetchFailure | null {
    return this.last;
  }
}

/** The one log the app's fetch wrapper writes to and the sign-in wait reads from. */
export const firebaseFetchFailures = new FirebaseFetchFailureLog();

// Firebase gives up on a request after at most 60 s, so a failure older than that cannot be what a wait is stuck on
const FIREBASE_FAILURE_LOOKBACK_MS = 60_000;

/** `<where> +<when, relative to the wait's start>ms <error name>` when the failure is recent enough to matter, else null. */
export function describeFirebaseFetchFailure(failure: FirebaseFetchFailure | null, startMs: number): string | null {
  if (!failure || failure.atMs < startMs - FIREBASE_FAILURE_LOOKBACK_MS) return null;
  return `${failure.where} +${Math.round(failure.atMs - startMs)}ms ${failure.errorName}`;
}

/** The real page. Reads browser globals lazily, so importing this module touches none. */
export function realAuthSettleEnvironment(): AuthSettleEnvironment {
  return {
    timers: realTimerEnvironment(),
    isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
    onHidden: cb => {
      if (typeof document === 'undefined') return () => { /* nothing to stop */ };
      const listener = () => {
        if (document.visibilityState === 'hidden') cb();
      };
      document.addEventListener('visibilitychange', listener);
      return () => document.removeEventListener('visibilitychange', listener);
    },
    firebaseRequestsSince: startMs => {
      // some older WebViews have no resource timing list at all
      const entries = typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('resource') as ResourceTimingLike[]
        : null;
      return summarizeFirebaseRequests(entries, startMs);
    },
    lastFirebaseFetchFailure: () => firebaseFetchFailures.latest(),
  };
}

/**
 * The same bounded wait as `awaitAuthStateSettledAsync` on running time, plus the facts a
 * timeout report needs (#6079).
 *
 * `onLateSettle` is called once, with how long after the limit fired Firebase did settle,
 * when the wait timed out and authStateReady() resolves later. It is never called when the
 * wait settled in time or ended on a rejection. Never throws, like the wait above.
 */
export async function measureAuthStateSettleAsync(
  fbAuth: Pick<FbAuthLike, 'authStateReady'>,
  timeoutMs: number,
  env: AuthSettleEnvironment,
  onLateSettle?: (lateByMs: number) => void,
): Promise<AuthSettleMeasurement> {
  const startMs = env.timers.nowMs();
  let hiddenDuringWait = env.isHidden();
  const stopWatchingVisibility = env.onHidden(() => { hiddenDuringWait = true; });
  // true when authStateReady() resolved, false when it rejected; a rejection is recorded, not thrown
  let rejection: string | null = null;
  const readyOutcome = fbAuth.authStateReady().then(() => true, err => {
    // keep what it rejected with so the report can say why; the wait itself ends unsettled
    rejection = describeError(err);
    return false;
  });
  // the cast keeps TypeScript from narrowing it to undefined: it is assigned inside a callback
  let limit = undefined as RunningTimeTimeoutHandle | undefined;
  let timedOut = false;
  let settled = false;
  try {
    // race auth settling against the running-time limit; a rejection ends the wait unsettled
    settled = await Promise.race([
      readyOutcome,
      new Promise<boolean>(resolve => {
        limit = scheduleRunningTimeTimeout(() => { timedOut = true; resolve(false); }, timeoutMs, env.timers);
      }),
    ]);
  } finally {
    stopWatchingVisibility();
    limit?.cancel();
  }
  const endMs = env.timers.nowMs();

  // if the limit fired, keep listening so a late settle can still be reported
  if (timedOut && onLateSettle) {
    void readyOutcome.then(resolved => {
      if (resolved) onLateSettle(Math.round(env.timers.nowMs() - endMs));
    });
  }

  return {
    settled,
    end: settled ? AuthSettleEnd.Settled : (timedOut ? AuthSettleEnd.TimedOut : AuthSettleEnd.Rejected),
    // a rejection that came after the limit belongs to a later moment, not to this wait
    rejection: timedOut ? null : rejection,
    waitWallMs: Math.round(endMs - startMs),
    notRunningMs: Math.round(limit?.notRunningMs() ?? 0),
    hiddenDuringWait,
    pageAgeMs: Math.round(startMs),
    firebaseRequests: settled ? null : env.firebaseRequestsSince(startMs),
    firebaseLastFetchFailure: settled ? null : describeFirebaseFetchFailure(env.lastFirebaseFetchFailure(), startMs),
  };
}

/**
 * Wait (up to timeoutMs) for Firebase to finish restoring its session, then report whether
 * a user is present. On timeout we fall through and read whatever currentUser is now —
 * preserving the original fast-fail semantics rather than blocking the login indefinitely.
 */
export async function awaitFirebaseUserPresentAsync(
  fbAuth: FbAuthLike,
  timeoutMs: number,
): Promise<FirebaseUserReadiness> {
  const settled = await awaitAuthStateSettledAsync(fbAuth, timeoutMs);
  return { present: fbAuth.currentUser != null, settled };
}
